import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/store/auth';
import api, { responses as responsesApi } from '@/api';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';
import { useInputDialog } from '@/components/ui/InputDialog';
import { useAppStore } from '@/store/app';
import { cn } from '@/utils';

// Import hooks
import {
  useResponseFull,
  useResponseVersions,
  useResponseActivity,
  useResponseComments,
  useUpdateResponse,
  useAddResponseComment,
  useUpdateResponseComment,
  useDeleteResponseComment,
  useApproveResponse,
  useRejectResponse,
  useSendResponse,
  useRecallResponse
} from '@/hooks/useResponses';

// Import components
import { SourceCorrespondencePanel } from '@/components/responses/detail/SourceCorrespondencePanel';
import { ResponseDraftEditor } from '@/components/responses/detail/ResponseDraftEditor';
import { WorkflowProgressTracker } from '@/components/responses/detail/WorkflowProgressTracker';
import { ResponseActivityTimeline } from '@/components/responses/detail/ResponseActivityTimeline';
import { ResponseCommentsSection } from '@/components/responses/detail/ResponseCommentsSection';
import { ResponseVersionHistory } from '@/components/responses/detail/ResponseVersionHistory';
import { FloatingActionBar } from '@/components/responses/detail/FloatingActionBar';

export const ResponseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { confirm } = useConfirmation();
  const { prompt } = useInputDialog();
  const { addNotification } = useAppStore();

  // State
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [approveComment, setApproveComment] = useState('');
  const [rejectComment, setRejectComment] = useState('');
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [releaseReason, setReleaseReason] = useState('');
  const [requestingRelease, setRequestingRelease] = useState(false);

  // Fetch data
  const {
    data: fullResponse,
    loading: isLoadingResponse,
    error: responseError,
    refetch: refetchResponse
  } = useResponseFull(id);

  const {
    data: versionsData,
    loading: isLoadingVersions,
    refetch: refetchVersions
  } = useResponseVersions(id);

  const {
    data: activityData,
    loading: isLoadingActivity,
    refetch: refetchActivity
  } = useResponseActivity(id);

  const {
    data: commentsData,
    loading: isLoadingComments,
    refetch: refetchComments
  } = useResponseComments(id);

  // Mutations
  const { mutate: updateResponse, isPending: isUpdating } = useUpdateResponse({
    onSuccess: () => {
      refetchResponse();
      refetchVersions();
      refetchActivity();
    }
  });

  const { mutate: addComment, isPending: isAddingComment } = useAddResponseComment({
    onSuccess: () => {
      refetchComments();
      refetchActivity();
    }
  });

  const { mutate: updateComment } = useUpdateResponseComment({
    onSuccess: () => {
      refetchComments();
    }
  });

  const { mutate: deleteComment } = useDeleteResponseComment({
    onSuccess: () => {
      refetchComments();
      refetchActivity();
    }
  });

  const { mutate: approveResponse, isPending: isApproving } = useApproveResponse({
    onSuccess: () => {
      setShowApproveModal(false);
      setApproveComment('');
      refetchResponse();
      refetchActivity();
    }
  });

  const { mutate: rejectResponse, isPending: isRejecting } = useRejectResponse({
    onSuccess: () => {
      setShowRejectModal(false);
      setRejectComment('');
      refetchResponse();
      refetchActivity();
    }
  });

  const { mutate: sendResponse, isPending: isSending } = useSendResponse({
    onSuccess: () => {
      refetchResponse();
      refetchActivity();
    }
  });

  const { mutate: recallResponse, isPending: isRecalling } = useRecallResponse({
    onSuccess: () => {
      refetchResponse();
      refetchActivity();
    }
  });

  // Close with Signed Letter state
  const [closureNote, setClosureNote] = useState('');
  const [closureMailContent, setClosureMailContent] = useState('');
  const [closureRecipients, setClosureRecipients] = useState('');
  const [closureFiles, setClosureFiles] = useState<File[]>([]);
  const [isClosing, setIsClosing] = useState(false);
  // The signed letter is often already in the register — it arrived back
  // through the sent-mail sync, or was filed by hand. Uploading it again
  // creates a second entry for the same letter, so offer to point at the
  // existing one instead (customer request #6).
  const [closureMode, setClosureMode] = useState<'upload' | 'select'>('upload');
  const [outwardOptions, setOutwardOptions] = useState<any[]>([]);
  const [loadingOutward, setLoadingOutward] = useState(false);
  const [selectedOutwardId, setSelectedOutwardId] = useState('');

  // Outward correspondences on the same project — the candidates for "this
  // letter is already filed". Resolved when asked for, not at render: the
  // response itself is loaded further down this component.
  const loadOutwardCorrespondences = async (closureProjectId: string | null) => {
    if (!closureProjectId) {
      addNotification?.({ type: 'warning', title: 'No project', message: 'This response has no project, so there is nothing to choose from.' });
      return;
    }
    setLoadingOutward(true);
    try {
      // The list endpoint answers { correspondences, pagination } and only
      // sorts on createdAt/updatedAt/classificationScore/lastAccessedAt.
      const res: any = await api.correspondences.list({
        projectId: closureProjectId,
        direction: 'outward',
        limit: 100,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      } as any);
      const rows = res?.correspondences || res?.data?.correspondences || (Array.isArray(res?.data) ? res.data : []);
      setOutwardOptions(rows);
      if (rows.length === 0) {
        addNotification?.({ type: 'info', title: 'No outward letters yet', message: 'This project has no outward correspondence to choose from — upload the signed letter instead.' });
      }
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Could not load correspondences', message: err?.message || 'Please try again.' });
    } finally {
      setLoadingOutward(false);
    }
  };

  const handleCloseWithSignedLetter = async () => {
    if (!id) return;
    if (closureMode === 'select' && !selectedOutwardId) {
      addNotification?.({ type: 'warning', title: 'Nothing selected', message: 'Choose the correspondence that is this signed letter.' });
      return;
    }
    setIsClosing(true);
    try {
      // Upload files first if any
      let attachmentIds: string[] = [];
      if (closureMode === 'upload' && closureFiles.length > 0) {
        const uploadResult = await api.attachments.upload({
          files: closureFiles,
          responseId: id,
        });
        const normalized = (uploadResult as any)?.data?.attachments || (uploadResult as any)?.attachments || uploadResult || [];
        attachmentIds = normalized
          .filter((r: any) => r.success !== false)
          // The upload route returns { attachment: { id, filename, ... } } — the id
          // field is the ObjectId. Mongoose virtuals also expose _id; check both.
          .map((r: any) => r.attachment?._id || r.attachment?.id || r._id || r.id)
          .filter(Boolean);
      }

      // Call the close endpoint
      const token = localStorage.getItem('authToken') || (() => {
        const store = localStorage.getItem('auth-store');
        return store ? JSON.parse(store)?.state?.token : null;
      })();
      const res = await fetch(`/api/responses/${id}/close-with-signed-letter`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          note: closureNote || undefined,
          mailContent: closureMailContent || undefined,
          recipients: closureRecipients || undefined,
          attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
          existingCorrespondenceId: closureMode === 'select' ? selectedOutwardId : undefined,
        }),
      });
      const data = await res.json();
      if (data.success) {
        addNotification?.({ type: 'success', title: 'Response Closed', message: 'Response closed with signed letter successfully.' });
        setClosureNote('');
        setClosureMailContent('');
        setClosureRecipients('');
        setClosureFiles([]);
        setSelectedOutwardId('');
        setClosureMode('upload');
        refetchResponse();
        refetchActivity();
      } else {
        throw new Error(data.error || 'Failed to close response');
      }
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Close Failed', message: err.message });
    } finally {
      setIsClosing(false);
    }
  };

  // Handlers
  const handleSaveDraft = (data: { mailSubject: string; mailBody: string; attachments?: string[] }) => {
    if (!id) return;
    // Backend requires an 'action' field for all response updates
    updateResponse({
      id,
      data: {
        ...data,
        action: 'Edited'
      }
    });
  };

  const handleAddComment = (content: string, stageName?: string) => {
    if (!id) return;
    addComment({ id, comment: content, stageName });
  };

  const handleUpdateComment = (commentId: string, content: string) => {
    if (!id) return;
    updateComment({ id, commentId, content });
  };

  const handleDeleteComment = (commentId: string) => {
    if (!id) return;
    deleteComment({ id, commentId });
  };

  const handleApprove = () => {
    if (!id) return;
    approveResponse({
      id,
      comment: approveComment,
      stageComplete: true
    });
  };

  const handleReject = () => {
    if (!id || !rejectComment.trim()) return;

    // Calculate previous stage to return to
    let returnToStage: string | undefined;
    const currentWorkflow = fullResponse?.workflow;
    const currentResponse = fullResponse?.response;
    if (currentWorkflow && currentResponse?.currentStage) {
      const stages = currentWorkflow.stages || [];
      const currentIndex = stages.findIndex((s: any) => s.stageName === currentResponse.currentStage);

      if (currentIndex > 0) {
        // Return to previous stage
        returnToStage = stages[currentIndex - 1].stageName;
      } else if (currentIndex === 0) {
        // If at first stage, stay at first stage but with Rejected status
        returnToStage = stages[0].stageName;
      }
    }

    rejectResponse({
      id,
      comment: rejectComment,
      returnToStage
    });
  };

  const handleSend = async () => {
    if (!id) return;
    const confirmed = await confirm({
      title: 'Send Response',
      message: 'Are you sure you want to send this response?',
      confirmText: 'Send',
      variant: 'warning',
    });
    if (confirmed) {
      sendResponse({ id });
    }
  };

  const handleRecall = async () => {
    if (!id) return;
    const reason = await prompt({
      title: 'Recall Response',
      message: 'Please provide a reason for recalling this response:',
      placeholder: 'Enter recall reason...',
      required: true,
      confirmText: 'Recall',
    });
    if (reason) {
      recallResponse({ id, reason });
    }
  };

  const handleSubmitForApproval = async () => {
    if (!id) return;
    const comment = await prompt({
      title: 'Submit for Approval',
      message: 'Optional comment for submission:',
      placeholder: 'Enter a comment...',
      confirmText: 'Submit',
    });
    if (comment === null) return;
    approveResponse({
      id,
      comment: comment || 'Submitted for approval',
      stageComplete: true
    });
  };

  const handleAddCorrespondence = async (correspondenceId: string, closureType: 'full' | 'partial') => {
    if (!id) return;
    await responsesApi.addCorrespondence(id, correspondenceId, closureType);
    refetchResponse();
  };

  const handleUpdateClosureType = async (correspondenceId: string, closureType: 'full' | 'partial') => {
    if (!id) return;
    try {
      await responsesApi.updateCorrespondenceClosure(id, correspondenceId, closureType);
      refetchResponse();
    } catch (error: any) {
      console.error('Failed to update closure type:', error);
      addNotification({ type: 'error', title: 'Update Failed', message: error?.message || 'Failed to update closure type. Please try again.' });
    }
  };

  const handleViewAttachment = (attachment: any) => {
    if (attachment.s3Url) {
      window.open(attachment.s3Url, '_blank');
    }
  };

  const handleViewVersion = (versionNumber: number) => {
    // TODO: Implement version viewer modal
  };

  const handleCompareVersions = (v1: number, v2: number) => {
    // TODO: Implement version comparison modal
  };

  const handleRequestRelease = async () => {
    if (!id) return;

    setRequestingRelease(true);
    try {
      await api.responses.requestRelease(id, releaseReason);
      setShowReleaseModal(false);
      setReleaseReason('');
      addNotification({ type: 'success', title: 'Release Requested', message: 'The lock owner will be notified.' });
      refetchResponse();
    } catch (error: any) {
      addNotification({ type: 'error', title: 'Request Failed', message: error?.response?.data?.message || error.message });
    } finally {
      setRequestingRelease(false);
    }
  };

  const handleGrantRelease = async (requestId: string) => {
    if (!id) return;

    const confirmed = await confirm({
      title: 'Grant Release',
      message: 'Are you sure you want to grant this release request? This will unlock the draft.',
      confirmText: 'Grant Release',
      variant: 'warning',
    });
    if (!confirmed) return;

    try {
      await api.responses.grantRelease(id, requestId);
      addNotification({ type: 'success', title: 'Release Granted', message: 'The draft has been unlocked.' });
      refetchResponse();
    } catch (error: any) {
      addNotification({ type: 'error', title: 'Grant Failed', message: error?.response?.data?.message || error.message });
    }
  };

  const handleDenyRelease = async (requestId: string) => {
    if (!id) return;

    const reason = await prompt({
      title: 'Deny Release',
      message: 'Reason for denying (optional):',
      placeholder: 'Enter reason...',
      confirmText: 'Deny',
    });
    if (reason === null) return;

    try {
      await api.responses.denyRelease(id, requestId, reason || undefined);
      addNotification({ type: 'info', title: 'Release Denied', message: 'Release request has been denied.' });
      refetchResponse();
    } catch (error: any) {
      addNotification({ type: 'error', title: 'Deny Failed', message: error?.response?.data?.message || error.message });
    }
  };

  // Loading state
  if (isLoadingResponse) {
    return (
      <PageLayout title="Response Details">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </PageLayout>
    );
  }

  // Error state
  if (responseError || !fullResponse) {
    return (
      <PageLayout title="Response Details">
        <ErrorAlert
          message={responseError?.message || 'Failed to load response'}
          onRetry={refetchResponse}
        />
      </PageLayout>
    );
  }

  const { response, sourceCorrespondences, workflow, currentStageApprovers, permissions } =
    fullResponse;

  return (
    <PageLayout
      title={(() => {
        const src = (response as any)?.sourceCorrespondences?.[0]?.correspondenceId;
        const ref = src?.documentReference || src?.documentCode;
        return `Response: ${ref ? ref + ' — ' : ''}${response.mailSubject || 'No Subject'}`;
      })()}
      actions={
        <div className="flex items-center space-x-2">
          {/* Back Button */}
          <Button
            onClick={() => navigate('/responses')}
            variant="ghost"
            size="sm"
          >
            ← Back to Responses
          </Button>

          {/* Send Button */}
          {permissions.canSend && (
            <Button
              onClick={handleSend}
              variant="primary"
              size="sm"
              disabled={isSending}
            >
              {isSending ? 'Sending...' : 'Send Response'}
            </Button>
          )}

          {/* Recall Button */}
          {permissions.canEdit && response.status !== 'Draft' && (
            <Button
              onClick={handleRecall}
              variant="outline"
              size="sm"
              disabled={isRecalling}
            >
              {isRecalling ? 'Recalling...' : 'Recall'}
            </Button>
          )}
        </div>
      }
    >
      {/* Three-Panel Responsive Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pb-24">
        {/* LEFT PANEL: Source Correspondence */}
        <div className="lg:col-span-3 space-y-6">
          <SourceCorrespondencePanel
            projectIds={((response as any)?.projectIds || [])
              .map((p: any) => (typeof p === 'string' ? p : p?._id))
              .filter(Boolean)}
            correspondences={sourceCorrespondences}
            onViewAttachment={handleViewAttachment}
            canEdit={permissions.canEdit}
            onAddCorrespondence={handleAddCorrespondence}
            onUpdateClosureType={handleUpdateClosureType}
          />
        </div>

        {/* CENTER PANEL: Response Editor */}
        <div className="lg:col-span-6 space-y-6">
          <ResponseDraftEditor
            response={response}
            canEdit={permissions.canEdit}
            onSave={handleSaveDraft}
            isSaving={isUpdating}
            workflow={workflow || undefined}
          />

          {/* Version History */}
          {versionsData && versionsData.versions && versionsData.versions.length > 0 && (
            <ResponseVersionHistory
              versions={versionsData.versions}
              currentVersion={versionsData.currentVersion}
              onViewVersion={handleViewVersion}
              onCompareVersions={handleCompareVersions}
              isLoading={isLoadingVersions}
            />
          )}

          {/* The letter this response went out as. Suraj asked for the Doc ID
              and the letter's own reference number next to the PDF, so the
              register entry and the response can be matched up at a glance. */}
          {(((response as any).outwardCorrespondences) || []).length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Sent as
              </h3>
              <div className="space-y-3">
                {((response as any).outwardCorrespondences || []).map((oc: any, i: number) => {
                  const corr = oc?.correspondenceId;
                  if (!corr || typeof corr !== 'object') return null;
                  const refField = (corr.customFields || [])
                    .find((f: any) => f?.fieldId === 'document_reference_number' && f?.value);
                  const reference = (refField?.value && String(refField.value).trim())
                    || (corr.documentReference && String(corr.documentReference).trim())
                    || null;
                  return (
                    <div key={corr._id || i} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                      {corr.documentCode && (
                        <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-mono text-xs">
                          {corr.documentCode}
                        </span>
                      )}
                      {reference ? (
                        <span className="font-medium text-gray-900 dark:text-white">{reference}</span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 text-xs">no reference number on the letter</span>
                      )}
                      <span className="text-gray-600 dark:text-gray-400 truncate max-w-full">
                        {corr.subject || 'No subject'}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {oc.source === 'upload' ? '(uploaded signed letter)' : '(linked from the register)'}
                      </span>
                      {corr._id && (
                        <a
                          href={`/correspondences/${corr._id}`}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          Open
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Close with Signed Letter — visible when response is in a finalized status */}
          {(['Complete', 'Approved', 'Sent'].includes(response.status)) && (() => {
            // The response's own project list first; then the source
            // correspondence (mapped by the hook, or the raw populated shape).
            const unwrap = (v: any): string | null => (v && typeof v === 'object' ? (v._id || v.id) : v) || null;
            const fromResponse = unwrap(((response as any)?.projectIds || [])[0]);
            const mapped: any = (sourceCorrespondences || [])[0];
            const raw: any = (response as any)?.sourceCorrespondences?.[0]?.correspondenceId;
            const closureProjectId: string | null =
              fromResponse || unwrap(mapped?.projectId) || unwrap(raw?.projectId) || null;
            return (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border-2 border-green-200 dark:border-green-800 p-6 space-y-4">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Close with Signed Letter</h3>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Record the signed letter and the outgoing mail details to close this response. Source correspondences will be marked as closed.
              </p>

              {/* Upload it, or point at the entry that is already in the register */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setClosureMode('upload')}
                  className={cn(
                    'px-3 py-1.5 rounded text-sm font-medium border',
                    closureMode === 'upload'
                      ? 'bg-green-600 border-green-600 text-white'
                      : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                  )}
                >
                  Upload signed letter
                </button>
                <button
                  type="button"
                  onClick={() => { setClosureMode('select'); if (outwardOptions.length === 0) loadOutwardCorrespondences(closureProjectId); }}
                  className={cn(
                    'px-3 py-1.5 rounded text-sm font-medium border',
                    closureMode === 'select'
                      ? 'bg-green-600 border-green-600 text-white'
                      : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                  )}
                >
                  Select from correspondences
                </button>
              </div>

              {closureMode === 'upload' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Signed Letter (PDF/Document)</label>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={(e) => setClosureFiles(Array.from(e.target.files || []))}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100 dark:file:bg-green-900/30 dark:file:text-green-300"
                  />
                  {closureFiles.length > 0 && (
                    <p className="mt-1 text-xs text-green-600">{closureFiles.length} file(s) selected</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    If this letter is already in the register, the system links to that entry instead of filing it twice.
                  </p>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Outward correspondence on this project
                    </label>
                    <button
                      type="button"
                      onClick={() => loadOutwardCorrespondences(closureProjectId)}
                      disabled={loadingOutward}
                      className="text-xs text-green-700 dark:text-green-400 hover:underline disabled:opacity-60"
                    >
                      {loadingOutward ? 'Loading…' : 'Refresh'}
                    </button>
                  </div>
                  <select
                    value={selectedOutwardId}
                    onChange={(e) => setSelectedOutwardId(e.target.value)}
                    disabled={loadingOutward}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">
                      {loadingOutward ? 'Loading…' : outwardOptions.length ? 'Select the letter…' : 'No outward correspondences on this project'}
                    </option>
                    {outwardOptions.map((c: any) => {
                      const ref = (c.customFields || []).find((f: any) => f.fieldId === 'document_reference_number')?.value
                        || c.documentReference;
                      const when = c.documentDate ? new Date(c.documentDate).toLocaleDateString('en-GB') : '';
                      return (
                        <option key={c._id} value={c._id}>
                          {[ref || c.documentCode, when, c.subject].filter(Boolean).join(' · ').slice(0, 120)}
                        </option>
                      );
                    })}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    No new entry is created — this response is linked to the letter you choose.
                  </p>
                </div>
              )}

              {/* Recipients */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Recipients</label>
                <input
                  type="text"
                  value={closureRecipients}
                  onChange={(e) => setClosureRecipients(e.target.value)}
                  placeholder="e.g., NHAI PIU Meerut, IE LASA Hapur"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Mail content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Outgoing Mail Content (optional)</label>
                <textarea
                  value={closureMailContent}
                  onChange={(e) => setClosureMailContent(e.target.value)}
                  rows={3}
                  placeholder="Content of the outgoing email/letter..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Closure Note (optional)</label>
                <input
                  type="text"
                  value={closureNote}
                  onChange={(e) => setClosureNote(e.target.value)}
                  placeholder="e.g., Signed by Project Head and dispatched"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleCloseWithSignedLetter}
                  disabled={isClosing}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {isClosing ? 'Closing...' : 'Close Response with Signed Letter'}
                </Button>
              </div>
            </div>
            );
          })()}
        </div>

        {/* RIGHT PANEL: Workflow & Activity */}
        <div className="lg:col-span-3 space-y-6">
          {/* Workflow Progress */}
          <WorkflowProgressTracker
            workflow={workflow}
            currentStage={response.currentStage}
            status={response.status}
            currentStageApprovers={currentStageApprovers}
            stageTransitionedAt={response.stageTransitionedAt}
            canApprove={permissions.canApprove}
            canReject={permissions.canReject}
            onApprove={() => setShowApproveModal(true)}
            onReject={() => setShowRejectModal(true)}
            draftLock={response.draftLock}
            canEdit={permissions.canEdit}
            onRequestRelease={() => setShowReleaseModal(true)}
            onGrantRelease={handleGrantRelease}
            onDenyRelease={handleDenyRelease}
          />

          {/* Activity Timeline */}
          {activityData && activityData.activities && (
            <ResponseActivityTimeline
              activities={activityData.activities}
              isLoading={isLoadingActivity}
            />
          )}

          {/* Comments */}
          {commentsData && commentsData.comments && (
            <ResponseCommentsSection
              comments={commentsData.comments}
              canComment={permissions.canComment}
              currentUserId={user?._id}
              onAddComment={handleAddComment}
              onUpdateComment={handleUpdateComment}
              onDeleteComment={handleDeleteComment}
              currentStage={response.currentStage}
              isLoading={isLoadingComments}
              isAdding={isAddingComment}
            />
          )}
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Approve Response
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to approve this response? It will move to the next stage or be marked as approved.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Comment (optional)
              </label>
              <textarea
                value={approveComment}
                onChange={(e) => setApproveComment(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                placeholder="Add an optional comment..."
              />
            </div>
            <div className="flex items-center justify-end space-x-2">
              <Button
                onClick={() => setShowApproveModal(false)}
                variant="ghost"
                disabled={isApproving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleApprove}
                variant="primary"
                disabled={isApproving}
              >
                {isApproving ? 'Approving...' : 'Approve'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Reject Response
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Please provide a reason for rejecting this response.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                placeholder="Enter rejection reason..."
                required
              />
            </div>
            <div className="flex items-center justify-end space-x-2">
              <Button
                onClick={() => setShowRejectModal(false)}
                variant="ghost"
                disabled={isRejecting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                variant="primary"
                disabled={!rejectComment.trim() || isRejecting}
                className="bg-red-600 hover:bg-red-700"
              >
                {isRejecting ? 'Rejecting...' : 'Reject'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Request Release Modal */}
      {showReleaseModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setShowReleaseModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Request Draft Release
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Ask the lock owner to release the draft
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason (optional)
                </label>
                <textarea
                  value={releaseReason}
                  onChange={(e) => setReleaseReason(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 resize-none"
                  placeholder="Explain why you need access to this draft..."
                />
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowReleaseModal(false)}
                  disabled={requestingRelease}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRequestRelease}
                  disabled={requestingRelease}
                  className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {requestingRelease ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Requesting...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                      </svg>
                      Request Release
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};
